import { useLocalSearchParams, useRouter } from 'expo-router';
import { useRef, useState } from 'react';
import { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import type { TableNextProps } from '@/components/Custom/TableNext.types';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import { graphql } from '@/gql/graphql';
import useDebounce from '@/hooks/useDebounce';
import usePaginatedList from '@/hooks/usePaginatedList';
import useRefetchOnFocus from '@/hooks/useRefetchOnFocus';
import {
  getUniqueIds,
  useSelectionClear,
  useSelectionSelectedIds,
  useSelectionStore,
  useSelectionToggle,
} from '@/hooks/useSelection';
import OrganizationAssignmentDashboard, {
  OrganizationAssignmentSelectionActions,
} from '@/screens/OrganizationAssignmentDashboard/OrganizationAssignmentDashboard';
import {
  getOrganizationAssignmentWhere,
  getOrganizationBadgeAssignmentColumns,
  type OrganizationAssignmentColumnAttribute,
  type OrganizationAssignmentRow,
  toOrganizationAssignmentRow,
} from '@/screens/OrganizationAssignmentDashboard/OrganizationAssignmentDashboard.utils';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';
import {
  type BadgeDetailAssignmentCreatePathname,
  getBadgeDetailRoutePathnames,
} from './BadgeDetail.utils';

const GetBadgeAssignmentDashboard = graphql(
  /* GraphQL */ `
    query GetBadgeAssignmentDashboard(
      $badgeId: uuid!
      $eventId: uuid
      $personId: String
      $teamId: uuid
      $assignmentWhere: badge_assignee_bool_exp!
      $assignmentLimit: Int!
      $assignmentOffset: Int!
    ) {
      badge_by_pk(id: $badgeId) {
        id
        can_assign(
          args: { event_id: $eventId, person_id: $personId, team_id: $teamId }
        )
        permissions
        badge_attributes(order_by: { order: asc }) {
          id
          attribute_type
          default_value
          title
          options
          is_required
          order
        }
      }
      badge_assignee(
        where: $assignmentWhere
        limit: $assignmentLimit
        offset: $assignmentOffset
        order_by: { updated_at: desc }
      ) {
        id
        can_assign
        person_id
        event_id
        team_id
        created_at
        updated_at
        person {
          id
          name
          color
          avatar_id
        }
        badge {
          id
          title
          color
        }
        event {
          id
          title
        }
        team {
          id
          title
        }
        badge_assignee_attributes(
          order_by: { badge_attribute: { order: asc } }
        ) {
          id
          badge_attribute_id
          value
          formatted_value
          badge_attribute {
            id
            title
          }
        }
      }
      badge_assignee_aggregate(where: $assignmentWhere) {
        aggregate {
          count
        }
      }
    }
  `,
);

const ASSIGNMENT_PAGE_LIMIT = 25;

export default function BadgeDetailMembers() {
  const { push } = useRouter();
  const scrollViewRef = useRef<ScrollView>(null);

  const { badgeId, eventId, personId, teamId } = useLocalSearchParams<{
    badgeId?: string;
    eventId?: string;
    personId?: string;
    teamId?: string;
  }>();
  const { assignmentCreatePathname, assignmentEditPathname } =
    getBadgeDetailRoutePathnames();
  const { value, debouncedValue, onChangeText, reset } = useDebounce(
    DEFAULT_SEARCH_MINIMUM,
  );
  const onChangeSearchText = (nextValue: string) => {
    onChangeText(nextValue);
  };
  const onPressCancelSearch = () => {
    reset();
  };
  const paginated = usePaginatedList({
    limit: ASSIGNMENT_PAGE_LIMIT,
    scrollViewRef,
  });
  const assignmentWhere = getOrganizationAssignmentWhere({
    badgeIds: badgeId ? [badgeId] : [],
    organizationId: eventId,
    personId,
    searchTarget: 'person',
    searchTerm: debouncedValue,
    teamIds: teamId ? [teamId] : [],
  });

  const [{ data, fetching }, refetchDashboard] = useQuery({
    query: GetBadgeAssignmentDashboard,
    variables: {
      badgeId: badgeId ?? '',
      eventId,
      personId,
      teamId,
      assignmentWhere,
      assignmentLimit: paginated.limit,
      assignmentOffset: paginated.offset,
    },
    requestPolicy: 'cache-and-network',
    pause: !badgeId || (!eventId && !teamId),
  });
  useRefetchOnFocus({
    enabled: Boolean(badgeId),
    refetch: () => {
      refetchDashboard({ requestPolicy: 'network-only' });
    },
  });

  const badge = data?.badge_by_pk;
  const badgeAttributes: OrganizationAssignmentColumnAttribute[] =
    badge?.badge_attributes ?? [];
  const assignments = data?.badge_assignee ?? [];
  const canAssign = Boolean(
    badge?.can_assign ||
      badge?.permissions?.assign_badge ||
      assignments.some((assignment) => assignment.can_assign),
  );
  const rows = assignments.map(toOrganizationAssignmentRow);
  const assignmentCount = data?.badge_assignee_aggregate.aggregate?.count ?? 0;
  const assignmentIds = rows.map((row) => row.assignmentId);
  const assignmentSelectionStore = useSelectionStore<string>();
  const clearSelection = useSelectionClear(assignmentSelectionStore);
  const selectedAssignmentIds = useSelectionSelectedIds(
    assignmentSelectionStore,
  );
  const toggleSelectedAssignment = useSelectionToggle(assignmentSelectionStore);
  const [assignmentSelectionMode, setAssignmentSelectionMode] = useState(false);
  const isAssignmentSelectionMode =
    assignmentSelectionMode || selectedAssignmentIds.length > 0;
  const columns = getOrganizationBadgeAssignmentColumns({
    assignmentCount: assignmentIds.length,
    canSelectAssignments: canAssign && isAssignmentSelectionMode,
    selectedBadgeAttributes: badgeAttributes,
    selectionStore: assignmentSelectionStore,
    testIDPrefix: 'BadgeDashboard',
    visibleAssignmentIds: assignmentIds,
  });
  const onPressAssignmentRow: TableNextProps<OrganizationAssignmentRow>['onPressRow'] =
    (row) => {
      if (isAssignmentSelectionMode) {
        toggleSelectedAssignment(
          row.assignmentId,
          !selectedAssignmentIds.includes(row.assignmentId),
        );
        return;
      }

      push({
        pathname: assignmentEditPathname,
        params: {
          assigneeId: row.assignmentId,
          eventId: row.eventId ?? eventId,
          teamId: row.teamId ?? teamId,
          personId: row.personId,
        },
      });
    };
  const onPressStartSelection = () => {
    setAssignmentSelectionMode(true);
  };
  const onPressEndSelection = () => {
    clearSelection();
    setAssignmentSelectionMode(false);
  };

  return (
    <ScrollView
      ref={scrollViewRef}
      contentContainerStyle={styles.content}
      contentInsetAdjustmentBehavior="automatic"
      keyboardShouldPersistTaps="always"
    >
      <Box flexGrow={1} px="lg" py="md">
        <OrganizationAssignmentDashboard
          fetching={fetching}
          actionContent={
            canAssign ? (
              <BadgeAssignmentActionContent
                assignmentCreatePathname={assignmentCreatePathname}
                badgeId={badgeId}
                canAssign={canAssign}
                eventId={eventId}
                isSelectionMode={isAssignmentSelectionMode}
                onPressClearSelection={onPressEndSelection}
                onPressEndSelection={onPressEndSelection}
                onPressStartSelection={onPressStartSelection}
                personId={personId}
                rows={rows}
                selectedAssignmentIds={selectedAssignmentIds}
                teamId={teamId}
              />
            ) : null
          }
          assignmentCount={assignmentCount}
          columns={columns}
          onChangeText={onChangeSearchText}
          onPressCancelSearch={onPressCancelSearch}
          onPressRow={onPressAssignmentRow}
          paginated={paginated}
          emptyMessage="No Matching Assignments"
          rows={rows}
          searchPlaceholder="Search Assignments..."
          searchValue={value}
        />
      </Box>
    </ScrollView>
  );
}

function BadgeAssignmentActionContent({
  assignmentCreatePathname,
  badgeId,
  canAssign,
  eventId,
  isSelectionMode,
  onPressClearSelection,
  onPressEndSelection,
  onPressStartSelection,
  personId,
  rows,
  selectedAssignmentIds,
  teamId,
}: {
  assignmentCreatePathname: BadgeDetailAssignmentCreatePathname;
  badgeId?: string;
  canAssign: boolean;
  eventId?: string;
  isSelectionMode: boolean;
  onPressClearSelection: () => void;
  onPressEndSelection: () => void;
  onPressStartSelection: () => void;
  personId?: string;
  rows: OrganizationAssignmentRow[];
  selectedAssignmentIds: string[];
  teamId?: string;
}) {
  const { push } = useRouter();
  const selectedPersonIds = getUniqueIds(
    rows.flatMap((row) =>
      selectedAssignmentIds.includes(row.assignmentId) && row.personId
        ? [row.personId]
        : [],
    ),
  );
  const onPressAssignSelected = () => {
    const selectedPersonId =
      selectedPersonIds.length === 1 ? selectedPersonIds[0] : personId;

    push({
      pathname: assignmentCreatePathname,
      params: {
        badgeId,
        eventId,
        personId: selectedPersonId,
        selected: selectedPersonIds.join(','),
        teamId,
      },
    });
  };
  const onPressAssignPerson = () => {
    if (!personId) {
      return;
    }

    push({
      pathname: assignmentCreatePathname,
      params: {
        badgeId,
        eventId,
        personId,
        teamId,
      },
    });
  };
  const onPressDeleteSelectedAssignments = () => {
    const assigneeId = selectedAssignmentIds[0];

    if (!assigneeId || !badgeId) {
      return;
    }

    push({
      pathname: '/badge-assignee/[assigneeId]/delete',
      params: {
        assigneeId,
        assigneeIds: selectedAssignmentIds.join(','),
        assignmentCount: String(selectedAssignmentIds.length),
      },
    });
    onPressClearSelection();
  };

  if (!canAssign) {
    return null;
  }

  if (selectedAssignmentIds.length) {
    return (
      <BadgeAssignmentActions
        onPressAssignSelected={onPressAssignSelected}
        onPressClearSelection={onPressClearSelection}
        onPressDeleteSelectedAssignments={onPressDeleteSelectedAssignments}
        selectedCount={selectedAssignmentIds.length}
      />
    );
  }

  if (isSelectionMode) {
    return (
      <ActionButton
        forceLabel
        icon="close-outline"
        label="Done"
        variant="secondary"
        testID="BadgeDashboardDoneSelectionButton"
        onPress={onPressEndSelection}
      />
    );
  }

  if (!personId) {
    return (
      <ActionButton
        disabled={!rows.length}
        forceLabel
        icon="checkbox-outline"
        label="Select"
        variant="secondary"
        testID="BadgeDashboardSelectAssignmentsButton"
        onPress={onPressStartSelection}
      />
    );
  }

  return (
    <>
      <ActionButton
        forceLabel
        icon="add-outline"
        label="Assign Badge"
        variant="success"
        testID="BadgeDashboardAssignPersonButton"
        onPress={onPressAssignPerson}
      />
      <ActionButton
        disabled={!rows.length}
        forceLabel
        icon="checkbox-outline"
        label="Select"
        variant="secondary"
        testID="BadgeDashboardSelectAssignmentsButton"
        onPress={onPressStartSelection}
      />
    </>
  );
}

function BadgeAssignmentActions({
  onPressAssignSelected,
  onPressClearSelection,
  onPressDeleteSelectedAssignments,
  selectedCount,
}: {
  onPressAssignSelected: () => void;
  onPressClearSelection: () => void;
  onPressDeleteSelectedAssignments: () => void;
  selectedCount: number;
}) {
  if (!selectedCount) {
    return null;
  }

  return (
    <>
      <OrganizationAssignmentSelectionActions
        onPressClearSelection={onPressClearSelection}
        selectedCount={selectedCount}
        testIDPrefix="BadgeDashboard"
      />
      <ActionButton
        forceLabel
        icon="add-outline"
        label="Assign Again"
        variant="success"
        testID="BadgeDashboardAssignSelectedRowsButton"
        onPress={onPressAssignSelected}
      />
      <ActionButton
        forceLabel
        icon="trash-outline"
        label="Delete"
        variant="error"
        testID="BadgeDashboardDeleteSelectedAssignmentsButton"
        onPress={onPressDeleteSelectedAssignments}
      />
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  content: {
    flexGrow: 1,
    paddingBottom: theme.space('xl'),
  },
}));
